import { useEffect, useRef } from "react";
import { CAPTCHA_CONFIG } from "constants/captcha";

/**
 * Loads reCAPTCHA v3 and v2 scripts dynamically and registers a global
 * callback for the v2 checkbox widget token.
 *
 * Usage: call once in the component that hosts the CAPTCHA flow.
 * The v2 callback setter is wired up here; the component controls when to render the widget.
 */
export const useCaptchaLoader = (
  captchaEnabled: boolean,
  onV2Token: (token: string) => void,
): {
  loadV3Captcha: () => Promise<void>;
  loadV2Captcha: () => Promise<void>;
} => {
  const onV2TokenRef = useRef(onV2Token);
  const v3ScriptPromiseRef = useRef<Promise<void> | null>(null);
  const v2ScriptPromiseRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    onV2TokenRef.current = onV2Token;
  }, [onV2Token]);

  useEffect(() => {
    (window as any).onV2CaptchaCallback = (token: string) =>
      onV2TokenRef.current(token);

    return () => {
      delete (window as any).onV2CaptchaCallback;
    };
  }, []);

  const loadV3Captcha = () => {
    if (!captchaEnabled || !CAPTCHA_CONFIG.siteKey) {
      return Promise.resolve();
    }

    if (!v3ScriptPromiseRef.current) {
      v3ScriptPromiseRef.current = new Promise<void>((resolve, reject) => {
        const v3Script = document.createElement("script");
        v3Script.src = `https://www.google.com/recaptcha/api.js?render=${CAPTCHA_CONFIG.siteKey}`;
        v3Script.async = true;
        v3Script.onload = () => {
          const grecaptcha = (window as any).grecaptcha;
          if (!grecaptcha?.ready) {
            reject(new Error("reCAPTCHA v3 is not ready"));
            return;
          }
          grecaptcha.ready(resolve);
        };
        v3Script.onerror = (error) => reject(error);
        document.head.appendChild(v3Script);
      });
    }

    return v3ScriptPromiseRef.current;
  };

  const loadV2Captcha = () => {
    if (!captchaEnabled || !CAPTCHA_CONFIG.v2SiteKey) {
      return Promise.resolve();
    }

    if (!v2ScriptPromiseRef.current) {
      v2ScriptPromiseRef.current = new Promise<void>((resolve, reject) => {
        const v2Script = document.createElement("script");
        v2Script.src = "https://www.google.com/recaptcha/api.js";
        v2Script.async = true;
        v2Script.onload = () => resolve();
        v2Script.onerror = (e) => reject(e);
        document.head.appendChild(v2Script);
      });
    }

    return v2ScriptPromiseRef.current;
  };

  return { loadV3Captcha, loadV2Captcha };
};
